// CaseDetails Component
// This file provides the main case details view, including patient, procedure, insurance, and authorization information.
// It handles editing, updating, and comparing data from multiple sources (Athena, Cerner).

import { useState, useEffect, useReducer } from "react";
import {
  Address,
  Demographics,
  Insurance,
  CaseDetailsBody,
  Procedure,
  AuthStatus,
  GetAuthorizationResponse,
} from "@/types/procedure";
import Accordion from "@/components/Accordion";
import ChipInput from "@/components/ChipInput";
import { AttentionIcon, EditIcon, AlertIcon } from "../Icons";
import {
  Button,
  SingleSelect,
  Input,
  Modal,
  StatusTag,
  Textarea,
  Toast,
} from "@Forge/dist";
import {
  objectDiff,
  BooleanMutation,
  hasDiff,
  compareStringArrays,
  formatDate,
} from "@/utils/helpers";
import { authStatusMapping } from "@/utils/mappers";
import {
  ColorWrapper,
  OrderDetailsContainer,
  Row,
  Section,
  TextItalic,
  ProcedureNameWrapper,
  LabeledComponent,
  WarningTemplate,
  InfoTemplate,
  HighlightContainer,
} from "./components.stitches";
import { useCaseDetails } from "@/hooks/use-case-details";
import useFormHistory from "@/hooks/use-form-history";
import Loader from "@/components/Loader";
import AuthStatusForm from "@/components/AuthStatusForm";
import { DateTime } from "luxon";
import { useLockout } from "@/hooks/use-feature-lockout";
import { useAppState } from "@/contexts/app-state";
import {
  generateAuthOptions,
  addressFormatter,
  renderInsurances,
  getAge,
} from "./helpers";

/**
 * Props for the CaseDetails component
 * @param caseId - The unique identifier for the case to display
 */
interface CaseDetailsProps {
  caseId: string;
}

/**
 * ModalType interface for modal state management
 */
interface ModalType {
  show: boolean;
  useLarge: boolean;
  headerText: string;
  renderForm: () => React.ReactNode;
}

const UNAVAILABLE_TEXT = "Unavailable";

const DEFAULT_DEMOGRAPHICS_DIFF: BooleanMutation<Demographics> = {
  sex: false,
  address: false,
  dob: false,
  patientName: false,
  lastUpdated: false,
  patientId: false,
};

const DEFAULT_ADDRESS_DIFF: BooleanMutation<Address> = {
  city: false,
  state: false,
  line1: false,
  line2: false,
  zip: false,
};

/**
 * CaseDetails Component
 * Displays and manages all details for a given case, including patient, procedure, insurance, and authorization info.
 * Handles editing, updating, and comparing data from Athena and Cerner systems.
 *
 * @param caseId - The unique identifier for the case
 */
const CaseDetails = ({ caseId }: CaseDetailsProps) => {
  const { loggedInUser } = useAppState();

  // --- API and context hooks ---
  // Fetches case details, update and authorization actions
  const {
    get: { data, isLoading, error, refetch, isRefetching },
    put: { updateCase, isPending },
    authorization: {
      verifyAuthorization,
      isPending: authPending,
      error: authError,
    },
  } = useCaseDetails(caseId);

  // Lockout management for concurrent editing
  const {
    get: { data: lockoutData, refetch: featureRefetch, isLockedOutForUser },
    post: { createLockout },
    delete: { removeLockout },
  } = useLockout(caseId, "update-case-details");

  // --- State variables ---
  // Toast notification data
  const [toastData, setToastData] = useState<{
    message: string;
    header: string;
    type: "success" | "attention" | "info";
  } | null>(null);

  // List of insurances currently assigned to the case
  const [workingInsurances, setWorkingInsurances] = useState<Insurance[]>([]);
  // List of all insurances available for the case
  const [allInsurances, setAllInsurances] = useState<Insurance[]>([]);

  // Tracks field-level differences between Athena and Cerner demographics
  const [demographicsDiff, setDemographicsDiff] = useState<
    BooleanMutation<Demographics>
  >(DEFAULT_DEMOGRAPHICS_DIFF);
  // Tracks field-level differences between Athena and Cerner addresses
  const [addressDiff, setAddressDiff] =
    useState<BooleanMutation<Address>>(DEFAULT_ADDRESS_DIFF);

  // Whether the procedure name is being edited
  const [isUpdatingProcedureName, setIsUpdatingProcedureName] = useState(false);
  // The updated procedure name (if being edited)
  const [updatedProcedureName, setUpdatedProcedureName] = useState<string>("");
  // Whether the confirmation modal is shown
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Form change history and helpers for tracking unsaved changes
  const { formChangeHistory, updateChangeHistory, resetFormChangeHistory } =
    useFormHistory();

  // CPT code chips and notes
  const [cptCodes, setCptCodes] = useState<string[]>([]); // List of CPT codes
  const [cptNotes, setCptNotes] = useState<string>(""); // Notes for CPT codes
  const [showCptNotes, setShowCptNotes] = useState(false); // Whether to show CPT notes textarea

  // ICD-10 code chips and notes
  const [icdCodes, setIcdCodes] = useState<string[]>([]); // List of ICD-10 codes
  const [icd10Notes, setIcd10Notes] = useState<string>(""); // Notes for ICD-10 codes
  const [showIcd10Notes, setShowIcd10Notes] = useState(false); // Whether to show ICD-10 notes textarea

  // Modal state for forms (e.g., AuthStatusForm)
  const [modal, setModal] = useReducer(
    (state: ModalType, changes: Partial<ModalType>) => ({
      ...state,
      ...changes,
    }),
    {
      show: false,
      useLarge: false,
      headerText: "",
      renderForm: () => null,
    },
  );

  // --- Effects ---

  // Show toast if there is an authorization error
  useEffect(() => {
    if (authError) {
      setToastData({
        message:
          "An error occurred while processing the request. Please try again later.",
        header: "Error Retrieving Authorization",
        type: "attention",
      });
    }
  }, [authError]);

  // Lockout management: create lockout if editing, remove if done
  useEffect(() => {
    if (!lockoutData && !isLockedOutForUser() && formChangeHistory.length) {
      createLockout();
    }
    if (lockoutData) {
      if (lockoutData?.userId === loggedInUser && !formChangeHistory.length) {
        removeLockout();
      }
    }
  }, [formChangeHistory]);

  // Track changes to procedure name for change history
  useEffect(() => {
    if (data) {
      const previousValue = data.procedureInformation.updatedProcedureName
        ? data.procedureInformation.updatedProcedureName
        : data.procedureInformation.scheduledprocedureName;

      updateChangeHistory(
        {
          field: "procedureName",
          previousValue: previousValue,
          newValue: updatedProcedureName,
          userMessage: `Updated Procedure Name to ${updatedProcedureName}`,
        },
        previousValue === updatedProcedureName || updatedProcedureName === "",
      );
    }
  }, [updatedProcedureName]);

  // When data loads or refetches, update all state and reset form history
  useEffect(() => {
    if (data && !isRefetching) {
      setAllInsurances(data?.allInsurances);
      setWorkingInsurances(data?.workingInsurances);
      if (data.athenaPatientDetails) {
        setDemographicsDiff(
          objectDiff<Demographics>(
            data?.athenaPatientDetails,
            data?.cernerPatientDetails,
          ),
        );
        setAddressDiff(
          objectDiff<Address>(
            data?.athenaPatientDetails.address,
            data?.cernerPatientDetails.address,
          ),
        );
      }

      setCptCodes(data?.procedureInformation.cptList ?? []);
      setIcdCodes(data?.procedureInformation.icd10List ?? []);
      setShowCptNotes(data?.procedureInformation.cptNotes !== null);
      setShowIcd10Notes(data?.procedureInformation.icd10Notes !== null);
      setCptNotes(data?.procedureInformation.cptNotes ?? "");
      setIcd10Notes(data?.procedureInformation.icd10Notes ?? "");
    }

    resetFormChangeHistory();
    setIsUpdatingProcedureName(false);
    setShowConfirmation(false);
  }, [data, isRefetching]);

  // Track changes to CPT codes for change history
  useEffect(() => {
    if (data) {
      updateChangeHistory(
        {
          field: "cptCodes",
          previousValue: data?.procedureInformation.cptList.join(", ") ?? "",
          newValue: cptCodes.join(", "),
          userMessage: `Updated CPT Codes to ${cptCodes.join(", ")}`,
        },
        compareStringArrays(cptCodes, data?.procedureInformation.cptList),
      );
    }
  }, [cptCodes]);

  // Track changes to ICD-10 codes for change history
  useEffect(() => {
    if (data) {
      updateChangeHistory(
        {
          field: "icd10Codes",
          previousValue: data?.procedureInformation.icd10List.join(", ") ?? "",
          newValue: icdCodes.join(", "),
          userMessage: `Updated ICD-10 Codes to ${icdCodes.join(", ")}`,
        },
        compareStringArrays(icdCodes, data?.procedureInformation.icd10List),
      );
    }
  }, [icdCodes]);

  // Track changes to CPT notes for change history
  useEffect(() => {
    if (data) {
      updateChangeHistory(
        {
          field: "cptNotes",
          previousValue: data?.procedureInformation.cptNotes ?? "",
          newValue: cptNotes,
          userMessage: `Updated CPT Notes to ${cptNotes}`,
        },
        cptNotes === data?.procedureInformation.cptNotes || cptNotes === "",
      );
    }
  }, [cptNotes]);

  // Track changes to ICD-10 notes for change history
  useEffect(() => {
    if (data) {
      updateChangeHistory(
        {
          field: "icdNotes",
          previousValue: data?.procedureInformation.icd10Notes ?? "",
          newValue: icd10Notes,
          userMessage: `Updated ICD-10 Notes to ${icd10Notes}`,
        },
        icd10Notes === data?.procedureInformation.icd10Notes ||
          icd10Notes === "",
      );
    }
  }, [icd10Notes]);

  // --- Handlers ---

  /**
   * Adds a CPT code chip to the list, ensuring no duplicates.
   * @param chip - The CPT code to add
   */
  const handleCPTAddition = (chip: string) => {
    const noDups = new Set([...cptCodes, chip]);
    setCptCodes(Array.from(noDups));
  };

  /**
   * Removes a CPT code chip from the list.
   * @param chip - The CPT code to remove
   */
  const handleCPTRemoval = (chip: string) => {
    setCptCodes(cptCodes.filter((c) => c !== chip));
  };

  /**
   * Adds an ICD-10 code chip to the list, ensuring no duplicates.
   * @param chip - The ICD-10 code to add
   */
  const handleICDAddition = (chip: string) => {
    const noDups = new Set([...icdCodes, chip]);
    setIcdCodes(Array.from(noDups));
  };

  /**
   * Removes an ICD-10 code chip from the list.
   * @param chip - The ICD-10 code to remove
   */
  const handleICDRemoval = (chip: string) => {
    setIcdCodes(icdCodes.filter((c) => c !== chip));
  };

  /**
   * Adds an insurance to the working insurances list and removes it from all insurances.
   * Updates the change history.
   * @param insurance - The insurance to add
   */
  const handleAddInsurance = (insurance: Insurance) => {
    setWorkingInsurances([...workingInsurances, insurance]);
    setAllInsurances(allInsurances.filter((ins) => ins !== insurance));

    updateChangeHistory(
      {
        field: "insurance" + insurance.insuranceNumber,
        previousValue: null,
        newValue: insurance.insuranceNumber,
        userMessage: `Added insurance: ${insurance.insuranceNumber} to working list.`,
      },
      !data?.allInsurances.find(
        (ins) => ins.insuranceNumber === insurance.insuranceNumber,
      ),
    );
  };

  /**
   * Removes an insurance from the working insurances list and adds it back to all insurances.
   * Updates the change history.
   * @param insurance - The insurance to remove
   */
  const handleRemoveInsurance = (insurance: Insurance) => {
    setWorkingInsurances(
      workingInsurances.filter(
        (ins) => ins.insuranceNumber !== insurance.insuranceNumber,
      ),
    );
    setAllInsurances([...allInsurances, insurance]);

    updateChangeHistory(
      {
        field: "insurance" + insurance.insuranceNumber,
        previousValue: insurance.insuranceNumber,
        newValue: "Removed",
        userMessage: `Removed insurance: ${insurance.insuranceNumber} from working list.`,
      },
      !data?.workingInsurances.find(
        (ins) => ins.insuranceNumber === insurance.insuranceNumber,
      ),
    );
  };

  // --- Diff helpers ---
  // Whether there are differences in demographics or address between Athena and Cerner
  const hasDemographicsDiff: boolean = hasDiff<Demographics>(demographicsDiff, {
    exclude: ["lastUpdated", "address", "patientId"],
  });
  const hasAddressDiff: boolean = hasDiff<Address>(addressDiff);

  /**
   * Renders the submit button for the form, handling lockout and change state.
   */
  const renderFormSubmitButton = () => {
    if (lockoutData && isLockedOutForUser())
      return (
        <Button disabled>
          Currently being updated by {lockoutData?.userId}
        </Button>
      );
    if (!formChangeHistory.length)
      return <Button disabled>No Changes to Submit</Button>;
    return (
      <div style={{ display: "grid", gap: "8px" }}>
        <Button variant="primary" onClick={() => setShowConfirmation(true)}>
          <AttentionIcon />
          {`${formChangeHistory.length} pending change${
            formChangeHistory.length > 1 ? "s" : ""
          } to submit`}
        </Button>
      </div>
    );
  };

  /**
   * Submits the case update, sending all changes to the backend and removing the lockout.
   */
  const handleCaseUpdateSubmit = () => {
    const body: CaseDetailsBody = {
      workingInsurances,
      allInsurances,
      cptList: cptCodes,
      icd10List: icdCodes,
    };

    if (cptNotes !== data?.procedureInformation.cptNotes)
      body.cptNotes = cptNotes;
    if (icd10Notes !== data?.procedureInformation.icd10Notes)
      body.icd10Notes = icd10Notes;

    if (updatedProcedureName) body.updatedProcedureName = updatedProcedureName;

    updateCase(body);
    removeLockout();
  };

  /**
   * Refreshes the case details and feature lockout state.
   */
  const refresh = () => {
    refetch()
      .then(() => {
        return featureRefetch();
      })
      .catch((err) => console.log(err));
  };

  /**
   * Opens the AuthStatusForm modal for changing the authorization status.
   * @param newStatus - The new authorization status
   * @param procedure - The procedure object
   */
  const handleAuthStatusChange = (
    newStatus: AuthStatus,
    procedure: Procedure,
  ) => {
    if ((newStatus as string) === "") return;
    setModal({
      show: true,
      useLarge: false,
      headerText: `Auth Status → ${newStatus}`,
      renderForm: () => (
        <AuthStatusForm
          procedure={procedure}
          newStatus={newStatus}
          onSubmit={() => setModal({ show: false })}
          onCancelClick={() => setModal({ show: false })}
        />
      ),
    });
  };

  /**
   * Requests the latest authorization number and shows a toast with the result.
   */
  const handleAuthRequest = () => {
    verifyAuthorization((data: GetAuthorizationResponse | undefined) => {
      if (data?.authorizationNumber) {
        setToastData({
          message: `Authorization Number: ${data.authorizationNumber}`,
          header: "Authorization Information",
          type: "success",
        });
      } else {
        setToastData({
          message:
            "The authorization number could not be found. Please try again later.",
          header: "No authorization info found",
          type: "attention",
        });
      }
      return Promise.resolve();
    });
  };

  /**
   * Renders the procedure name and edit button, or an input if editing.
   */
  const renderProcedureName = () => {
    if (!data) return null;
    if (!isUpdatingProcedureName) {
      return (
        <>
          <h2>
            {data.procedureInformation.updatedProcedureName ??
              `Scheduled as: ${
                data.procedureInformation.scheduledprocedureName ??
                "No Procedure Name Available"
              }`}
          </h2>
          <Button
            data-testid="edit-procedure-name"
            variant="tertiary"
            disabled={isLockedOutForUser()}
            onClick={() => setIsUpdatingProcedureName(true)}
            style={{
              filter: isLockedOutForUser()
                ? "grayscale(1) brightness(200%)"
                : "none",
            }}
          >
            <EditIcon />
          </Button>
        </>
      );
    }
    return (
      <Input
        clearable
        type="text"
        placeholder="Name Of Procedure"
        defaultValue={data.procedureInformation.updatedProcedureName ?? ""}
        onChange={(e) => setUpdatedProcedureName(e.target.value)}
      />
    );
  };

  /**
   * Renders the scheduled procedure name if editing or if an updated name exists.
   */
  const renderScheduledName = () => {
    if (!data) return null;
    if (
      data.procedureInformation.updatedProcedureName ||
      isUpdatingProcedureName
    ) {
      return (
        <h4>
          Scheduled as: {data.procedureInformation.scheduledprocedureName}
        </h4>
      );
    }
  };

  /**
   * Renders the authorization number or a button to request it, with self-pay logic.
   */
  const renderAuthButton = () => {
    if (!data) return null;

    if (data.workingInsurances.length === 0) {
      return "No insurance assigned";
    }

    if (
      data.workingInsurances.length > 0 &&
      data.workingInsurances[0].insurancePlan === "*SELF PAY*"
    ) {
      return <p style={{ fontStyle: "italic" }}>Self-pay: auth not required</p>;
    }

    return (
      data.authInfo.authorizationNumber ?? (
        <Button
          variant="secondary"
          onClick={handleAuthRequest}
          showLoadingSpinner={authPending}
          disabled={authPending}
        >
          {authPending ? "Requesting Auth Info..." : "Request Auth Info"}
        </Button>
      )
    );
  };

  // --- Derived state ---
  // Whether the case is self-pay (no insurance required)
  const isSelfPay =
    data &&
    data.workingInsurances.length > 0 &&
    data.workingInsurances[0].insurancePlan !== "*SELF PAY*";

  return (
    <Loader
      displayText="Getting up to date case details..."
      isLoading={isLoading || isRefetching}
      errorReason={error?.message}
    >
      <Modal
        width="medium"
        alertType="attention"
        show={showConfirmation}
        includeButtonRow={false}
        headerText="Confirm Changes"
        onHide={() => setShowConfirmation(false)}
      >
        <ol>
          {formChangeHistory.map((change) => (
            <li key={change.field}>{change.userMessage}</li>
          ))}
        </ol>
        <Row css={{ gap: "8px", padding: "10px" }}>
          <Button
            variant="primary"
            showLoadingSpinner={isPending}
            onClick={handleCaseUpdateSubmit}
          >
            Confirm
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowConfirmation(false);

              refresh();
            }}
          >
            <AlertIcon /> Discard All Changes
          </Button>
        </Row>
      </Modal>
      {data && (
        <OrderDetailsContainer style={{ paddingBottom: "65px" }}>
          {!data.authInfo.authorized && isSelfPay && (
            <HighlightContainer
              css={{
                justifyContent: "space-between",
              }}
            >
              <WarningTemplate
                padded={false}
                text={`Current Authorization obtained will not cover Surgery Date of ${formatDate(
                  {
                    date: data.procedureInformation.performDate,
                    format: "DD",
                  },
                )}`}
              />
            </HighlightContainer>
          )}
          <Row
            reverse
            css={{
              gap: "8px",
              position: "absolute",
              right: "0",
              left: "0",
              bottom: "0",
              zIndex: "2001",
              backgroundColor: "rgb(248, 248, 248)",
              padding: "16px 24px",
            }}
          >
            <Button
              icon="Refresh"
              onClick={refresh}
              text="Refresh"
              variant="secondary"
            />
            {renderFormSubmitButton()}
          </Row>
          <Section border={!!data.athenaPatientDetails}>
            <Row>
              <ProcedureNameWrapper>
                <Row css={{ gap: "8px" }}>{renderProcedureName()}</Row>
                {renderScheduledName()}
              </ProcedureNameWrapper>
            </Row>
            {data.athenaPatientDetails && (
              <Row>
                <LabeledComponent
                  label="AUTH STATUS"
                  component={
                    <SingleSelect
                      options={generateAuthOptions(data)}
                      value={{
                        label: authStatusMapping[data.authInfo.authStatus],
                        value: data.authInfo.authStatus,
                      }}
                      onChange={(e) => {
                        e.preventDefault();
                        handleAuthStatusChange(
                          e.target.value?.value as AuthStatus,
                          data,
                        );
                      }}
                    />
                  }
                />
                <LabeledComponent
                  label="SCHED STATUS"
                  component={data.scheduledStatus ?? UNAVAILABLE_TEXT}
                />
                <LabeledComponent
                  label="AUTH NUMBER"
                  component={renderAuthButton()}
                />
                <LabeledComponent
                  label="START DATE"
                  component={formatDate({
                    date: data.authInfo.beginDate,
                    format: "DDD",
                  })}
                />
                <LabeledComponent
                  label="END DATE"
                  icon={
                    !data.authInfo.authorized && isSelfPay && <AttentionIcon />
                  }
                  component={formatDate({
                    date: data.authInfo.endDate,
                    format: "DDD",
                  })}
                />
              </Row>
            )}
          </Section>
          <Section border>
            <Row>
              <h3>Procedure Information</h3>
            </Row>
            <Row>
              <LabeledComponent
                label="SURGERY DATE"
                icon={
                  !data.authInfo.authorized && isSelfPay && <AttentionIcon />
                }
                component={formatDate({
                  date: data.procedureInformation.performDate,
                  format: "FFF",
                })}
              />
              <LabeledComponent
                label="SURGEON"
                component={
                  data.procedureInformation.surgeonName ?? UNAVAILABLE_TEXT
                }
              />
              <LabeledComponent
                label="FACILITY"
                component={
                  data.procedureInformation.facilityName ?? UNAVAILABLE_TEXT
                }
              />
            </Row>
            {data.athenaPatientDetails && (
              <Row>
                <LabeledComponent
                  label="CPT"
                  component={
                    <div
                      style={{
                        maxWidth: "350px",
                      }}
                    >
                      <ChipInput
                        chips={cptCodes}
                        onAddChip={handleCPTAddition}
                        onChipRemoval={handleCPTRemoval}
                      />
                      {showCptNotes ? (
                        <LabeledComponent
                          containerStyles={{
                            marginTop: "16px",
                          }}
                          label="CPT Comments"
                          component={
                            <Textarea
                              value={cptNotes}
                              placeholder="Type comments here..."
                              onChange={(e) => setCptNotes(e.target.value)}
                            />
                          }
                        />
                      ) : (
                        <Button
                          style={{ width: "max-content" }}
                          text="Add a comment"
                          variant="tertiary"
                          onClick={() => setShowCptNotes(true)}
                        />
                      )}
                    </div>
                  }
                />
                <LabeledComponent
                  label="ICD-10"
                  component={
                    <div
                      style={{
                        maxWidth: "350px",
                      }}
                    >
                      <ChipInput
                        chips={icdCodes}
                        onAddChip={handleICDAddition}
                        onChipRemoval={handleICDRemoval}
                      />
                      {showIcd10Notes ? (
                        <LabeledComponent
                          containerStyles={{
                            marginTop: "16px",
                          }}
                          label="ICD-10 Comments"
                          component={
                            <Textarea
                              value={icd10Notes}
                              placeholder="Type comments here..."
                              onChange={(e) => setIcd10Notes(e.target.value)}
                            />
                          }
                        />
                      ) : (
                        <Button
                          style={{ width: "max-content" }}
                          text="Add a comment"
                          variant="tertiary"
                          onClick={() => setShowIcd10Notes(true)}
                        />
                      )}
                    </div>
                  }
                />
              </Row>
            )}
          </Section>
          <Section border={!!data.athenaPatientDetails}>
            <Row spaceBetween>
              <h3>Patient Information</h3>
              {data.athenaPatientDetails && (
                <TextItalic>
                  {formatDate({
                    date: data.athenaPatientDetails.lastUpdated,
                    format: "FFF",
                    unavailableText:
                      "Last updated date in Athena not available",
                    prefix: "Updated ",
                    postfix: " in Athena",
                  })}
                </TextItalic>
              )}
            </Row>
            {data.athenaPatientDetails ? (
              <>
                <Row>
                  <LabeledComponent
                    label="PATIENT"
                    component={data.athenaPatientDetails.patientName}
                  />
                  <LabeledComponent
                    label="DOB"
                    component={DateTime.fromFormat(
                      data.athenaPatientDetails.dob,
                      "yyyy-MM-dd",
                    ).toFormat("D")}
                  />
                  <LabeledComponent
                    label="SEX"
                    component={data.athenaPatientDetails.sex}
                  />
                </Row>
                <Row>
                  <LabeledComponent
                    label="ADDRESS 1"
                    component={addressFormatter(
                      data.athenaPatientDetails.address,
                    )}
                  />
                  <LabeledComponent
                    label="AGE"
                    component={getAge(data.athenaPatientDetails.dob)}
                  />
                </Row>
                <Row>
                  <Accordion
                    labelText={{
                      open: "Open Cerner Details",
                      close: "Close Cerner Details",
                    }}
                    icon={
                      hasAddressDiff || hasDemographicsDiff ? (
                        <AttentionIcon />
                      ) : null
                    }
                  >
                    <ColorWrapper>
                      <Row>
                        <LabeledComponent
                          highlight={demographicsDiff["patientName"]}
                          label="PATIENT"
                          component={data.cernerPatientDetails.patientName}
                        />
                        <LabeledComponent
                          highlight={demographicsDiff["dob"]}
                          label="DOB"
                          component={DateTime.fromFormat(
                            data.cernerPatientDetails.dob,
                            "yyyy-MM-dd",
                          ).toFormat("D")}
                        />
                        <LabeledComponent
                          highlight={demographicsDiff["sex"]}
                          label="SEX"
                          component={data.cernerPatientDetails.sex}
                        />
                      </Row>
                      <Row>
                        <LabeledComponent
                          label="ADDRESS 1"
                          component={addressFormatter(
                            data.cernerPatientDetails.address,
                            addressDiff,
                          )}
                        />
                        <LabeledComponent
                          label="AGE"
                          component={getAge(data.cernerPatientDetails.dob)}
                        />
                      </Row>
                      <Row reverse>
                        <TextItalic>
                          {formatDate({
                            date: data.cernerPatientDetails.lastUpdated,
                            format: "FFF",
                            unavailableText:
                              "Last updated date in Cerner not available",
                            prefix: "Updated ",
                            postfix: " in Cerner",
                          })}
                        </TextItalic>
                      </Row>
                    </ColorWrapper>
                  </Accordion>
                </Row>
              </>
            ) : (
              <>
                <Row
                  css={{
                    gap: "12px",
                    padding: "10px",
                    alignItems: "center",
                    svg: {
                      height: "50px",
                      width: "50px",
                    },
                  }}
                >
                  <AlertIcon />
                  <LabeledComponent
                    label="Patient not available in Athena."
                    component="Please register the patient in Athena and refresh to view updated details."
                  />
                </Row>
                <div
                  style={{
                    border: "1px solid #B6B7B7",
                    borderRadius: "3px",
                    padding: "16px",
                  }}
                >
                  <Row>
                    <h3
                      style={{
                        fontSize: "18px",
                        marginBottom: "13px",
                      }}
                    >
                      Cerner Details
                    </h3>
                  </Row>
                  <Row style={{ marginBottom: "24px" }}>
                    <LabeledComponent
                      label="PATIENT"
                      component={data.cernerPatientDetails.patientName}
                    />

                    <LabeledComponent
                      label="DOB"
                      component={data.cernerPatientDetails.dob}
                    />
                    <LabeledComponent
                      label="AGE"
                      component={getAge(data.cernerPatientDetails.dob)}
                    />
                  </Row>
                  <Row style={{ marginBottom: "24px" }}>
                    <LabeledComponent
                      label="ADDRESS 1"
                      component={addressFormatter(
                        data.cernerPatientDetails.address,
                      )}
                    />
                    <LabeledComponent
                      label="SEX"
                      component={data.cernerPatientDetails.sex}
                    />
                  </Row>
                  <Row>
                    <LabeledComponent
                      label="Cerner Insurance"
                      component={
                        <Row>
                          {renderInsurances(data?.cernerInsurances, {
                            onEmpty: (
                              <WarningTemplate text="No insurance information available in Cerner." />
                            ),
                          })}
                        </Row>
                      }
                    />
                  </Row>
                  <Row reverse style={{ marginTop: "24px" }}>
                    <TextItalic>
                      {data.cernerPatientDetails.lastUpdated
                        ? `Updated ${DateTime.fromISO(
                            data.cernerPatientDetails.lastUpdated,
                          ).toFormat("FFF")} in Cerner`
                        : "Last updated date in Cerner not available"}
                    </TextItalic>
                  </Row>
                </div>
                <InfoTemplate text="Changes in Athena may take up to 5 minutes to be reflected. If the patient's details are not appearing, please wait and try again." />
              </>
            )}
          </Section>
          {data.athenaPatientDetails && (
            <Section border>
              <Row>
                <h3>Insurance Information</h3>
              </Row>
              <Row>
                <LabeledComponent
                  containerStyles={{ width: "100%" }}
                  label=""
                  component={renderInsurances(workingInsurances, {
                    icon: "Unassign",
                    onIconClick: handleRemoveInsurance,
                    onEmpty: (
                      <WarningTemplate
                        text="Please select at least one working insurance for this
    patient."
                      />
                    ),
                    iconDisabled: isLockedOutForUser(),
                    badge: (
                      <StatusTag
                        text="Assigned to Auth"
                        color="positive"
                        secondary
                        style={{ height: "24px" }}
                      />
                    ),
                  })}
                />
              </Row>
              <Row>
                <LabeledComponent
                  containerStyles={{ width: "100%" }}
                  label=""
                  component={renderInsurances(allInsurances, {
                    icon: "Assign",
                    onEmpty:
                      workingInsurances.length === 0 ? (
                        <WarningTemplate text="There are no insurances available for this patient." />
                      ) : undefined,
                    onIconClick: handleAddInsurance,
                    iconDisabled: isLockedOutForUser(),
                    badge: (
                      <StatusTag
                        text="Not Assigned"
                        secondary
                        style={{ height: "24px" }}
                      />
                    ),
                    disableAlert: true,
                  })}
                />
              </Row>
              <Row>
                <Accordion
                  labelText={{
                    open: "Open Cerner Insurance",
                    close: "Close Cerner Insurance",
                  }}
                >
                  <ColorWrapper>
                    <Row>
                      {renderInsurances(data?.cernerInsurances, {
                        onEmpty: (
                          <WarningTemplate text="No insurance information available in Cerner." />
                        ),
                        mini: true,
                      })}
                    </Row>
                  </ColorWrapper>
                </Accordion>
              </Row>
            </Section>
          )}
        </OrderDetailsContainer>
      )}
      <Modal
        show={modal.show}
        headerText={modal.headerText}
        includeButtonRow={false}
        onHide={() => setModal({ show: false })}
        width={modal.useLarge ? "large" : "small"}
      >
        {modal.renderForm()}
      </Modal>
      {toastData && (
        <Toast
          id="details-toast"
          headerText={toastData?.header}
          alertType={toastData?.type}
          onDismiss={() => setToastData(null)}
        >
          {toastData?.message}
        </Toast>
      )}
    </Loader>
  );
};

export default CaseDetails;
